import { Fragment } from 'react';

import {
  BANDS_DJS_TAXONOMY_KEY,
  VendorTaxonomyKey,
  VENUES_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { setLocalStorage } from '@zola-helpers/client/dist/es/util/storage';
import { CreateInquiryRequest } from '@zola/svc-marketplace-ts-types';
import { ProductClicked } from '@zola/tracking-contracts/src/events';
import { Grid } from '@zola/zola-ui/src/components/Grid';
import { useResponsiveDesign } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignContext';

import styled from '@emotion/styled';
import cx from 'classnames';

import { cardViewToVendorCardVendor } from '~/components/common/cards/util/helper';
import VendorCard from '~/components/common/cards/VendorCard/VendorCard';
import { isSearchLocation } from '~/util/searchUtils';

import { SearchResultCardView, VendorSearchRequest } from '../SearchResults/types/types';
import { QuizCard } from './QuizCard';
import { VendorCardGridProps } from './types';
import { isQuizCardView } from './utils';

import styles from './vendorCardGrid.module.less';

const StyledGrid = styled(Grid)`
  grid-template-columns: [end] 1fr;
  grid-template-rows: auto;

  @media (min-width: 768px) and (max-width: 1100px) {
    grid-template-columns: 1fr 1fr [end] 1fr;
  }
  @media (min-width: 1100px) {
    grid-template-columns: 1fr 1fr 1fr [end] 1fr;
  }
`;

export const getCardKey = (card: SearchResultCardView) =>
  isQuizCardView(card) ? card.name : card.storefrontUuid;

export const getSearchLocation = (vendorSearch: VendorSearchRequest) => {
  const { selectedLocation, selectedTaxonomy } = vendorSearch || {};

  // Returning null will show the vendor's location vs. the searched location
  if (!isSearchLocation(selectedLocation) || selectedTaxonomy?.key === VENUES_TAXONOMY_KEY) {
    return null;
  }

  return selectedLocation.label;
};

const CardGrid: React.FC = ({ children }) => {
  return (
    <StyledGrid className={styles.vendorCardGrid} bordered={false}>
      {children}
    </StyledGrid>
  );
};

const FEATURED: CreateInquiryRequest.CtaPlacementEnum = 'FEATURED' as const;

export const getVendorCardGridLazyLoadLimit = (isMobile: boolean) => (isMobile ? 3 : 12);

const CardList = ({
  searchResults,
  isLoading,
  queriedLocation,
  listingShelfName,
  vendorSearch,
}: {
  searchResults: SearchResultCardView[];
  isLoading: boolean;
  offset: number;
  queriedLocation: string;
  listingShelfName: ProductClicked['listing_shelf_name'];
  vendorSearch: VendorSearchRequest;
}) => {
  const { availabilityDates, selectedLocation, selectedTaxonomy } = vendorSearch;
  const searchLocation = getSearchLocation(vendorSearch);
  const showServiceLocation = selectedLocation?.type === 'CITY';
  const { isMobile } = useResponsiveDesign();

  return (
    <Fragment>
      {searchResults.map((searchResultCardView, index) => {
        const isQuizCard = isQuizCardView(searchResultCardView);
        const isFirstQuizCard = isQuizCard && searchResults.findIndex(isQuizCardView) === index;
        return (
          <div
            key={getCardKey(searchResultCardView)}
            id={`vendor-card-${getCardKey(searchResultCardView)}`}
            data-testid="search-result"
            className={cx(styles.vendorCard, {
              [styles.quizCard]: isQuizCard,

              // Position the quiz cards in the grid if there are enough results to
              // allow for it.
              //
              // On mobile, the first quiz cards goes in slot 4, otherwise, it goes at
              // the end of the 2nd row, which has either 3 or 4 cards, so we need
              // 7 other cards to be able to position it there.  If we have fewer than
              // 7 cards, it just goes into the grid positionally.
              //
              // On mobile, the second quiz card goes in slot 12, otherwise it goes
              // at the end of the 7th row, which has either 3 or 4 cards, so we need,
              // in the worst case scenario, 27 other cards to be able to position it
              //
              // So anyway, we require the _largest_ number of search results to
              // position the quiz cards using css positioning, which is 2 rows * 4
              // cards for the first card (8 results) or 7 rows * 4 cards for the
              // second row. Reminder the quiz cards are part of the search results
              // at this point
              [styles.quizCard1]: searchResults.length >= 8 && isQuizCard && isFirstQuizCard,
              [styles.quizCard2]: searchResults.length >= 28 && isQuizCard && !isFirstQuizCard,
              [styles.blurCard]: isLoading,
            })}
          >
            {isQuizCard ? (
              <QuizCard
                card={searchResultCardView}
                taxonomyKey={selectedTaxonomy?.key as VendorTaxonomyKey}
              />
            ) : (
              <VendorCard
                noMargin
                vendor={cardViewToVendorCardVendor(searchResultCardView)}
                availabilityDates={availabilityDates}
                searchLocation={searchLocation}
                queriedLocation={queriedLocation}
                showServiceLocation={showServiceLocation}
                position={index + 1}
                listingShelfName={listingShelfName}
                trackClick
                onClick={() => {
                  if (
                    'featuredPlacement' in searchResultCardView &&
                    searchResultCardView.featuredPlacement
                  ) {
                    setLocalStorage(
                      `inquiry_entrypoint_${searchResultCardView.storefrontUuid}`,
                      FEATURED
                    );
                  }
                }}
                showAward
                lazyload={index >= getVendorCardGridLazyLoadLimit(isMobile)}
                showVendorType={vendorSearch.selectedTaxonomy?.key === BANDS_DJS_TAXONOMY_KEY}
              />
            )}
          </div>
        );
      })}
    </Fragment>
  );
};

// Code pen illustrating the grid concept: https://codepen.io/John-Naegle/pen/MWzRNQg
export const VendorCardGrid = ({ vendorSearch, ...rest }: VendorCardGridProps) => {
  const { selectedLocation } = vendorSearch;

  // Different from searchLocation in that it doesn't return null if the taxonomy type is Venues
  // This is meant to display the queried location when a user is filtering vendors by metro types
  const queriedLocation = selectedLocation?.label || '';

  return (
    <CardGrid>
      <CardList {...rest} vendorSearch={vendorSearch} queriedLocation={queriedLocation} />
    </CardGrid>
  );
};
